import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface EmotionData {
  name: string;
  count: number;
  percentage: number;
}

interface EmotionDistributionProps {
  data: EmotionData[];
}

const EmotionDistribution: React.FC<EmotionDistributionProps> = ({ data }) => {
  const { t } = useTranslation();

  // Debug: afficher les données reçues
  console.log('EmotionDistribution data:', data);

  // Styles de couleurs indépendants des émotions
  const colorStyles = [
    {
      gradient: 'linear-gradient(90deg, #68f1d1, #ff9800)',
      solid: '#ff9800'
    },
    {
      gradient: 'linear-gradient(90deg, #2E3192, #9B6DFF)',
      solid: '#9B6DFF'
    },
    {
      gradient: 'linear-gradient(90deg, #FF8E53, #FF4B8C)',
      solid: '#FF4B8C'
    },
    {
      gradient: 'linear-gradient(90deg, #9B6DFF, #68f1d1)',
      solid: '#68f1d1'
    },
    {
      gradient: 'linear-gradient(90deg, #FF4B8C, #ffb74d)',
      solid: '#ffb74d'
    }
  ];

  // Fonction pour obtenir le style de couleur en fonction de l'index
  const getColorStyle = (index: number) => {
    return colorStyles[index % colorStyles.length];
  };

  // Vérifier si les données sont valides
  if (!Array.isArray(data)) {
    console.error('EmotionDistribution: data is not an array', data);
    return null;
  }

  if (data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
        {t('statistics.emotionDistribution.noData')}
      </Box>
    );
  }

  // Trouver le nombre maximum d'occurrences
  const maxCount = Math.max(...data.map(item => item.count));

  // Préparation des données avec pourcentages, limitée aux 5 premières émotions
  const emotionsWithPercentages = data
    .slice(0, 5) // Limite aux 5 premières émotions
    .map(item => ({
      ...item,
      percentage: (item.count / maxCount) * 100
    }));

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      width: '100%',
      p: 2
    }}>
      
      {emotionsWithPercentages.map((item, index) => {
        // Vérifier si l'item est valide
        if (!item || typeof item !== 'object') {
          console.error('Invalid item in EmotionDistribution:', item);
          return null;
        }

        const colorStyle = getColorStyle(index);

        return (
          <Box key={item.name || index} sx={{ width: '100%' }}>
            <Box sx={{ 
              mb: 1,
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <Typography sx={{ 
                color: colorStyle.solid,
                fontSize: '1rem',
                fontWeight: 400
              }}>
                {item.name || 'Inconnue'}
              </Typography>
              <Typography sx={{ 
                color: colorStyle.solid,
                fontSize: '0.9rem',
                fontWeight: 400
              }}>
                {item.count || 0}×
              </Typography>
            </Box>
            <Box sx={{ 
              width: '100%', 
              height: 10,
              position: 'relative',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 10
            }}>
              <Box
                sx={{                  
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: `${item.percentage}%`,
                  background: colorStyle.gradient,
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: -10,
                    width: 20,
                    height: 20,
                    border: '4px solid ' + colorStyle.solid,
                    backgroundColor: "#1A1B35",
                    borderRadius: '50%'
                  }
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default EmotionDistribution;
